import { enqueueSnackbar } from "notistack";

class ErrorService {
  constructor() {
    this.errorHistory = [];
  }
  // Обработка HTTP ошибок
  handleApiError(error, customMessage = null) {
    let message = customMessage;
    let details = "";

    if (error.response) {
      // Сервер ответил с кодом ошибки
      const status = error.response.status;
      const data = error.response.data || {};

      // Проверяем новую структуру ошибок с error_code
      if (
        data.detail &&
        typeof data.detail === "object" &&
        data.detail.error_code
      ) {
        const errorCode = data.detail.error_code;
        switch (errorCode) {
          case "FOUNDER_EMAIL_EXISTS":
            message =
              "Пользователь с таким email уже зарегистрирован в системе";
            break;
          case "FOUNDER_NOT_FOUND":
            message = "Основатель не найден";
            break;
          case "BUSINESS_TYPE_NOT_FOUND":
            message = "Выбранный тип бизнеса не существует";
            break;
          case "SUBSCRIPTION_EXPIRED":
            message = "Подписка истекла";
            break;
          case "INVALID_SUBSCRIPTION_PLAN":
            message = "Неверный план подписки";
            break;
          case "UNAUTHORIZED":
            message = "Не авторизован";
            break;
          case "FORBIDDEN":
            message = "Доступ запрещен";
            break;
          case "DATABASE_ERROR":
            message = "Ошибка базы данных";
            break;
          case "INTERNAL_ERROR":
            message = "Внутренняя ошибка сервера";
            break;
          case "RATE_LIMIT_EXCEEDED":
            message = data.detail.message || "Слишком много запросов. Угомонись, отдохни немного! 😊";
            // Показываем уведомление, но НЕ выбрасываем из системы
            this.showRateLimitNotification(message, "warning");
            return message; // Already displayed as a warning.
          case "AUTH_RATE_LIMIT_EXCEEDED":
            message = data.detail.message || "Слишком много попыток входа. Попробуйте через 10 минут";
            // Показываем уведомление, но НЕ выбрасываем из системы
            this.showRateLimitNotification(message, "warning");
            return message; // Already displayed as a warning.
          default:
            message = data.detail.message || "Неизвестная ошибка";
        }

        details = JSON.stringify(data.detail.details || {}, null, 2);
      } else {
        // Старая обработка для совместимости
        switch (status) {
          case 400:
            if (data.detail) {
              if (typeof data.detail === 'string' && data.detail.includes("email")) {
                message = "Пользователь с таким email уже существует";
              } else if (typeof data.detail === 'string' && data.detail.includes("username")) {
                message = "Пользователь с таким именем уже существует";
              } else {
                message = typeof data.detail === 'string' ? data.detail : data.detail.message || 'Неверные данные';
              }
            } else {
              message = "Неверные данные";
            }
            break;
          case 401:
            message = "Неавторизованный доступ";
            break;
          case 403:
            message = "Доступ запрещен";
            break;
          case 404:
            message = "Ресурс не найден";
            break;
          case 409:
            message = "Конфликт данных - возможно, запись уже существует";
            break;
          case 422:
            if (data.detail && Array.isArray(data.detail)) {
              // Ошибки валидации FastAPI
              const validationErrors = data.detail
                .map((err) => {
                  const field = err.loc ? err.loc.join(".") : "поле";
                  return `${field}: ${err.msg}`;
                })
                .join(", ");
              message = `Ошибка валидации: ${validationErrors}`;
            } else {
              message = "Ошибка валидации данных";
            }
            break;
          case 500:
            message = "Внутренняя ошибка сервера";
            break;
          default:
            message = `Ошибка сервера (${status})`;
        }
      }

      details = JSON.stringify(data, null, 2);
    } else if (error.request) {
      // Запрос был отправлен, но ответа не получено
      message = "Нет связи с сервером. Проверьте подключение к интернету";
      details = "Сервер не отвечает";
    } else {
      // Ошибка настройки запроса
      message = "Ошибка при отправке запроса";
      details = error.message;
    }

    message = customMessage || message;
    // Сохраняем в историю ошибок
    this.logError({
      timestamp: new Date().toISOString(),
      message,
      details,
      error: error.toString(),
      url: error.config?.url || "unknown",
      method: error.config?.method || "unknown",
    });

    return message;
  }

  // Показать ошибку пользователю
  showError(error, customMessage = null) {
    const message = this.handleApiError(error, customMessage);
    const code = error.response?.data?.detail?.error_code;
    if (code === 'RATE_LIMIT_EXCEEDED' || code === 'AUTH_RATE_LIMIT_EXCEEDED') return message;
    enqueueSnackbar(message, {
      variant: "error",
      autoHideDuration: 5000,
      preventDuplicate: true,
    });
    return message;
  }

  // Показать успешное уведомление
  showSuccess(message) {
    enqueueSnackbar(message, {
      variant: "success",
      autoHideDuration: 3000,
    });
  }

  // Показать предупреждение
  showWarning(message) {
    enqueueSnackbar(message, {
      variant: "warning",
      autoHideDuration: 4000,
    });
  }

  // Показать информационное сообщение
  showInfo(message) {
    enqueueSnackbar(message, {
      variant: "info",
      autoHideDuration: 3000,
    });
  }

  // Логировать ошибку в историю
  logError(errorData) {
    this.errorHistory.unshift(errorData);
    // Ограничиваем историю 100 записями
    if (this.errorHistory.length > 100) {
      this.errorHistory = this.errorHistory.slice(0, 100);
    }

    // Details remain in the explicit error-history UI; do not dump them to console.
  }

  // Получить историю ошибок
  getErrorHistory() {
    return this.errorHistory;
  }

  // Очистить историю ошибок
  clearErrorHistory() {
    this.errorHistory = [];
  }

  // Экспорт ошибок для отладки
  exportErrors() {
    const dataStr = JSON.stringify(this.errorHistory, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `error-log-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  // Проверка состояния сервера
  async checkServerHealth() {
    try {
      const response = await fetch("/health");
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  // Показать уведомление о превышении лимита запросов
  showRateLimitNotification(message, severity = "warning") {
    enqueueSnackbar(message, {
      variant: severity,
      autoHideDuration: 8000, // Показываем дольше для rate limit
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center',
      },
      style: {
        fontSize: '16px',
        fontWeight: 'bold'
      }
    });
    
  }
}

// Создаем единственный экземпляр сервиса
const errorService = new ErrorService();

export default errorService;
